import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { Seo } from '@/components/Seo';
import { supabase } from '@/lib/supabaseClient';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import type { Article } from '@/types/article';
import { ARROW, ARTICLE_FIELDS, formatDate, useReveal } from './shared';
import '../../home/v2/home-v2.css';
import './content-v2.css';

// New-design Content hub (/content): a journal-style page with full-photo
// cards three to a row (newest first), category tabs with counts and a closing
// call to the 3-Step routine. Articles come from rxk_articles (Admin > Content).

const ALL = 'All';

export default function ContentV2() {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState(ALL);
  const root = useReveal<HTMLDivElement>();

  useEffect(() => {
    supabase
      .from('rxk_articles')
      .select(ARTICLE_FIELDS)
      .eq('status', 'published')
      .order('published_date', { ascending: false })
      .then(({ data }) => {
        setArticles((data as Article[]) ?? []);
        setLoading(false);
      });
  }, []);

  const cats = useMemo(() => {
    const m = new Map<string, number>();
    articles.forEach((a) => m.set(a.category, (m.get(a.category) ?? 0) + 1));
    return [...m.entries()];
  }, [articles]);

  const list = cat === ALL ? articles : articles.filter((a) => a.category === cat);
  const href = (a: Article) => `${prefix}/content/${a.slug}`;

  return (
    <>
      <Seo pageKey="content" />
      <SharedNavbar />
      <main className="w-full min-h-screen bg-white">
        <div ref={root} className="v2 cnt" style={{ paddingTop: 'var(--v2h-h, 64px)' }}>
          {/* Head: a journal masthead */}
          <header className="chead">
            <div className="wrap">
              <div className="chl">
                <span className="lbl rv">{t('content.hero.label')}</span>
                <h1 className="rv" style={{ ['--d' as string]: '.06s' }}>
                  {t('content.hero.heading')}
                </h1>
              </div>
              <div className="chr rv" style={{ ['--d' as string]: '.14s' }}>
                <p>{t('content.hero.subtitle')}</p>
                {!loading && articles.length > 0 && (
                  <dl className="cstat">
                    <div>
                      <dt>{String(articles.length).padStart(2, '0')}</dt>
                      <dd>{t('content.v2.articles', 'articles')}</dd>
                    </div>
                    <div>
                      <dt>{String(cats.length).padStart(2, '0')}</dt>
                      <dd>{t('content.v2.topics', 'topics')}</dd>
                    </div>
                    <div className="by">
                      <dt>
                        <svg viewBox="0 0 24 24" aria-hidden>
                          <path d="M5 12.5l4.5 4.5L19 7.5" />
                        </svg>
                      </dt>
                      <dd>{t('content.v2.by', 'Written by our pharmacists')}</dd>
                    </div>
                  </dl>
                )}
              </div>
            </div>
          </header>

          {/* Category filter */}
          {!loading && articles.length > 0 && (
            <nav className="ctabs" aria-label={t('content.categories')}>
              <div className="wrap">
                <div className="chips">
                  {[[ALL, articles.length] as [string, number], ...cats].map(([c, n]) => (
                    <button key={c} type="button" className={c === cat ? 'on' : ''} onClick={() => setCat(c)} aria-pressed={c === cat}>
                      {c === ALL ? t('content.allArticles') : c}
                      <span>{n}</span>
                    </button>
                  ))}
                </div>
                <span className="ccount">
                  {t('content.v2.showing', 'Showing')} <b>{list.length}</b>
                </span>
              </div>
            </nav>
          )}

          <section className="cbody">
            <div className="wrap">
              {loading ? (
                <div className="cskel" aria-hidden>
                  <div />
                  <div />
                </div>
              ) : list.length === 0 ? (
                <p className="cempty">{t('content.v2.empty', 'No articles in this category yet.')}</p>
              ) : (
                <>
                  {/* Photo cards, three to a row */}
                  <div className="pgrid">
                    {list.map((a, i) => (
                      <Link key={a.slug} to={href(a)} className="pcard rv" style={{ ['--d' as string]: `${(i % 3) * 0.08}s` }}>
                        {a.image && <img src={a.image} alt={a.title} loading={i < 3 ? undefined : 'lazy'} />}
                        <div className="ptop">
                          <span className="pcat" style={{ ['--c' as string]: a.category_color || '#8a1c2b' }}>
                            <i />
                            {a.category}
                          </span>
                          {i === 0 && cat === ALL ? (
                            <span className="pnew">{t('content.v2.latest', 'Latest')}</span>
                          ) : (
                            <span className="pno">{String(i + 1).padStart(2, '0')}</span>
                          )}
                        </div>
                        <div className="pbot">
                          <small>
                            {formatDate(a.published_date, currentLang)}
                            {a.read_time && ` · ${a.read_time}`}
                          </small>
                          <h3>{a.title}</h3>
                          <div className="pmore">
                            {a.excerpt && <p>{a.excerpt}</p>}
                            <span className="cmore">
                              {t('content.v2.read', 'Read article')}
                              <svg viewBox="0 0 24 24">
                                <path d={ARROW} />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Closing call */}
          <section className="cband">
            <div className="wrap rv">
              <div>
                <span className="lbl">{t('content.v2.bandLabel', 'From reading to routine')}</span>
                <h2>{t('content.v2.bandTitle', 'Put the science on your skin.')}</h2>
                <p>{t('content.v2.bandText', "The 3-Step Daily Treatment — Open, Treat, Lock — made for Bangladesh's climate.")}</p>
              </div>
              <div className="cbtns">
                <Link to={prefix + '/3-step-treatment'} className="btn dark">
                  {t('content.v2.bandSet', 'Discover the 3-Step')}
                </Link>
                <Link to={prefix + '/products'} className="btn ghost">
                  {t('content.v2.bandShop', 'Shop products')}
                </Link>
              </div>
            </div>
          </section>
        </div>
        <SharedFooter />
      </main>
    </>
  );
}
